// src/components/Common/ActionButton.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencilAlt,
  faTrash,
  faPaperPlane,
  faPhone,
  faEnvelope,
  faPlus,
  faKey,
  faCheck,
  faTimes,
  faSave,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import CustomTooltip from './CustomTooltip';
import { useTranslation } from 'react-i18next';
import './ActionButton.css';

const iconMap = {
  faPencil: faPencilAlt,
  faTrash: faTrash,
  faPaperPlane: faPaperPlane,
  faPhone: faPhone,
  faEnvelope: faEnvelope,
  faPlus: faPlus,
  faKey: faKey,
  faCheck: faCheck,
  faTimes: faTimes,
  faSave: faSave,
  faDownload: faDownload,
};

const ActionButton = ({
  onClick,
  label,
  text,
  icon,
  isMobile,
  colorType,
  as,
  href,
  disabled,
  ariaLabel,
  type,
  target,
  rel,
}) => {
  const { t } = useTranslation();

  const isAnchor = as === 'a';

  const renderElement = () => {
    const isIconOnly = icon && !text;
    const classes = [
      'action-button',
      colorType === 'primary' ? 'primary' : 'secondary',
      isMobile ? 'action-button-mobile' : '',
      isIconOnly ? 'icon-only' : '',
    ].join(' ');

    const commonProps = {
      className: classes,
      'aria-label': ariaLabel || label || t('actionButton.defaultLabel'),
      disabled: isAnchor ? undefined : disabled,
      ...(isAnchor ? { href } : { onClick }),
      ...(isAnchor ? { target, rel } : { type }),
    };

    const content = (
      <>
        {icon && <FontAwesomeIcon icon={iconMap[icon]} />}
        {text && <span className="button-text">{text}</span>}
      </>
    );

    if (isAnchor) {
      return <a {...commonProps}>{content}</a>;
    }

    return <button {...commonProps}>{content}</button>;
  };

  const tooltipContent =
    disabled ? t('actionButton.noActionAvailable') : label || t('actionButton.defaultLabel');

  // Show tooltip only if not mobile and the button is icon-only
  const shouldShowTooltip = !isMobile && icon && !text;

  if (shouldShowTooltip) {
    return <CustomTooltip text={tooltipContent}>{renderElement()}</CustomTooltip>;
  }

  return renderElement();
};

ActionButton.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string,
  text: PropTypes.string,
  icon: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  colorType: PropTypes.oneOf(['primary', 'secondary']),
  as: PropTypes.oneOf(['button', 'a']),
  href: (props, propName, componentName) => {
    if (props.as === 'a' && !props[propName]) {
      return new Error(
        `The prop \`${propName}\` is required when \`as='a'\` is specified in \`${componentName}\`.`
      );
    }
    return null;
  },
  target: (props, propName, componentName) => {
    if (props.as === 'a' && props[propName] && typeof props[propName] !== 'string') {
      return new Error(`The prop \`${propName}\` must be a string in \`${componentName}\`.`);
    }
    return null;
  },
  rel: (props, propName, componentName) => {
    if (props.as === 'a' && props[propName] && typeof props[propName] !== 'string') {
      return new Error(`The prop \`${propName}\` must be a string in \`${componentName}\`.`);
    }
    return null;
  },
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  target: PropTypes.string,
  rel: PropTypes.string,
};

ActionButton.defaultProps = {
  label: '',
  text: '',
  icon: null,
  colorType: 'secondary',
  as: 'button',
  href: null,
  target: '_self',
  rel: 'noopener noreferrer',
  disabled: false,
  ariaLabel: '',
  type: 'button',
};

export default ActionButton;
